import { Component, Output, EventEmitter, OnDestroy, HostListener, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ChipModule } from 'primeng/chip';
import { HttpClient } from '@angular/common/http';
import { TickerContextService } from '../services/ticker-context.service';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface SearchResult {
  symbol: string;
  name: string;
  type: string;
  exchange: string;
  currency: string;
  region?: string;
}

@Component({
    selector: 'app-stock-search',
    imports: [CommonModule, FormsModule, InputTextModule, ChipModule],
    template: `
    <div class="stock-search">
      <div class="search-input-wrap">
        <i class="pi pi-search search-icon"></i>
        <input
          pInputText
          type="text"
          name="stock-ticker-search"
          [(ngModel)]="query"
          (ngModelChange)="onQueryChange($event)"
          (focus)="onFocus()"
          placeholder="Search symbols or companies..."
          class="search-input"
          autocomplete="new-password"
        />
        <i class="pi pi-times clear-icon" *ngIf="query" (click)="clear()"></i>
      </div>

      <!-- PIN — docked active-ticker context bar. Replaces the old loose
           recent-ticker pill strip that floated under the top bar. Shown only
           in the global top bar (showActiveContext) when a ticker is in focus
           and the dropdown is closed, so it reads as intentional page context,
           attached to the search, rather than a stray tag hanging in space. -->
      <div class="active-ticker-bar"
           *ngIf="showActiveContext && tickerCtx.activeTicker() && !showDropdown"
           data-testid="active-ticker-bar">
        <i class="pi pi-chart-line atb-icon"></i>
        <button type="button" class="atb-ticker" (click)="openActiveTicker()"
                data-testid="active-ticker-open">{{ tickerCtx.activeTicker() }}</button>
        <span class="atb-hint">Viewing · tap for analysis</span>
        <button type="button" class="atb-clear"
                (click)="clearActiveTicker(); $event.stopPropagation()"
                aria-label="Clear active ticker" data-testid="active-ticker-clear">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Dropdown -->
      <div class="search-dropdown" [class.inline]="inlineResults" *ngIf="showDropdown">

        <!-- Quick recent tickers — relocated from the old floating strip into
             the dropdown so nothing hangs under the search when it is closed. -->
        <div *ngIf="!query && recentTickers.length > 0" class="dropdown-section recent-ticker-chips">
          <p-chip *ngFor="let ticker of recentTickers"
                  [label]="ticker"
                  [removable]="true"
                  (onRemove)="removeRecent(ticker)"
                  (onClick)="onRecentTickerClick(ticker)"
                  styleClass="recent-ticker-chip">
          </p-chip>
        </div>

        <!-- Recent searches (empty query) -->
        <div *ngIf="!query && recentSearches.length > 0" class="dropdown-section">
          <div class="section-header">
            <span>Recent</span>
            <button class="clear-history-btn" (click)="clearHistory(); $event.stopPropagation()">Clear</button>
          </div>
          <div *ngFor="let r of recentSearches" class="result-row recent-row" (click)="selectResult(r)">
            <i class="pi pi-clock" style="font-size:0.75rem; color:var(--text-color-secondary)"></i>
            <span class="result-symbol">{{ r.symbol }}</span>
            <span class="result-name">{{ r.name }}</span>
          </div>
        </div>

        <!-- Hint when query < 2 chars -->
        <div *ngIf="query && query.length < 2" class="empty-state">
          Type {{ 2 - query.length }} more character to search...
        </div>

        <!-- Live results -->
        <ng-container *ngIf="query && query.length >= 2">
          <div *ngIf="loading" class="loading-row">
            <i class="pi pi-spin pi-spinner"></i>&nbsp;Searching...
          </div>
          <div *ngIf="!loading && results.length === 0" class="no-results">
            No results for &ldquo;{{ query }}&rdquo;
          </div>
          <ng-container *ngIf="!loading && results.length > 0">
            <div *ngFor="let r of results" class="result-row" (click)="selectResult(r)">
              <span class="result-symbol">{{ r.symbol }}</span>
              <span class="result-name">{{ r.name.length > 45 ? (r.name | slice:0:45) + '…' : r.name }}</span>
              <span class="result-meta">
                <span class="result-type">{{ r.type }}</span>
                <span class="result-exchange">{{ r.exchange || '—' }}</span>
              </span>
            </div>
          </ng-container>
          <div *ngIf="cachedIndicator" class="cache-indicator">
            <i class="pi pi-database"></i>&nbsp;Cached results
          </div>
        </ng-container>

        <!-- Empty focus (no query, no history) -->
        <div *ngIf="!query && recentSearches.length === 0" class="empty-state">
          Start typing to search symbols (min. 2 chars)
        </div>
      </div>
    </div>
  `,
    styles: [`
    :host { display: block; }

    .stock-search {
      position: relative;
      width: 100%;
      max-width: 400px;
    }

    .search-input-wrap {
      position: relative;
      display: flex;
      align-items: center;
    }

    .search-icon {
      position: absolute;
      left: 0.75rem;
      color: var(--text-color-secondary);
      font-size: 0.875rem;
      z-index: 1;
      pointer-events: none;
    }

    .search-input {
      width: 100%;
      padding-left: 2.25rem !important;
      padding-right: 2rem !important;
    }

    .clear-icon {
      position: absolute;
      right: 0.75rem;
      cursor: pointer;
      color: var(--text-color-secondary);
      font-size: 0.75rem;
    }
    .clear-icon:hover { color: var(--text-color); }

    .search-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background: var(--surface-0);
      border: 1px solid var(--surface-border);
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      z-index: 2000;
      max-height: 360px;
      overflow-y: auto;
    }

    /* Inline mode — used when the search lives inside a clipped/scrolling
       overlay (e.g. the watchlist popover). An absolutely-positioned dropdown
       gets clipped by the overlay's overflow:hidden box and the mobile keyboard
       shrinks it further, so on a phone the results are invisible and the search
       looks dead (SCRUM bug: "search does nothing"). In flow, the results push
       the panel taller and the overlay's own scroll reveals them — nothing is
       clipped. */
    .search-dropdown.inline {
      position: static;
      left: auto;
      right: auto;
      z-index: auto;
      margin-top: 6px;
      box-shadow: none;
      max-height: 300px;
    }

    .dropdown-section { padding: 0.5rem 0; }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.25rem 1rem;
      font-size: 0.7rem;
      color: var(--text-color-secondary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .clear-history-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--primary-color);
      font-size: 0.7rem;
      padding: 0;
    }

    .result-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 1rem;
      cursor: pointer;
      transition: background 0.12s;
    }
    .result-row:hover { background: var(--surface-100); }

    .result-symbol {
      font-weight: 700;
      color: var(--primary-color);
      min-width: 64px;
      font-size: 0.875rem;
      flex-shrink: 0;
    }

    .result-name {
      flex: 1;
      color: var(--text-color);
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .result-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 1px;
      flex-shrink: 0;
    }

    .result-type,
    .result-exchange {
      font-size: 0.68rem;
      color: var(--text-color-secondary);
    }

    .loading-row, .no-results, .empty-state {
      padding: 1rem;
      text-align: center;
      color: var(--text-color-secondary);
      font-size: 0.875rem;
    }

    .cache-indicator {
      padding: 0.25rem 1rem;
      font-size: 0.68rem;
      color: var(--text-color-secondary);
      border-top: 1px solid var(--surface-border);
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .recent-row { color: var(--text-color-secondary); }

    /* Recent ticker chips (#16) */
    .recent-ticker-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
      margin-top: 0.4rem;
    }
    :host ::ng-deep .recent-ticker-chip {
      cursor: pointer;
      font-size: 0.8rem;
      font-weight: 600;
    }
    :host ::ng-deep .recent-ticker-chip:hover {
      background: var(--primary-100, #dbeafe);
    }

    /* PIN — docked active-ticker context bar, attached to the bottom edge of
       the search so it reads as an intentional extension of the field, not a
       loose pill floating over the page. */
    .active-ticker-bar {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      z-index: 30;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.35rem 0.6rem;
      border-radius: 8px;
      background: var(--surface-card, #fff);
      border: 1px solid var(--surface-border);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      font-size: 0.82rem;
    }
    .active-ticker-bar .atb-icon { color: var(--primary-color); font-size: 0.85rem; }
    .active-ticker-bar .atb-ticker {
      border: none; background: none; cursor: pointer;
      font-weight: 700; letter-spacing: 0.3px;
      color: var(--primary-color); font-size: 0.9rem; padding: 0;
    }
    .active-ticker-bar .atb-ticker:hover { text-decoration: underline; }
    .active-ticker-bar .atb-hint {
      color: var(--text-color-secondary); font-size: 0.75rem;
    }
    .active-ticker-bar .atb-clear {
      margin-left: auto; border: none; background: none; cursor: pointer;
      color: var(--text-color-secondary); display: flex; align-items: center;
      padding: 0.15rem; border-radius: 4px;
    }
    .active-ticker-bar .atb-clear:hover {
      color: var(--text-color); background: var(--surface-100);
    }
  `]
})
export class StockSearchComponent implements OnDestroy {
  @Output() symbolSelected = new EventEmitter<SearchResult>();
  // PIN — only the global top-bar instance shows the docked active-ticker bar.
  // Other placements (e.g. the Analysis dashboard header) already show their
  // own "will analyze" line, so they leave this off to avoid duplication.
  @Input() showActiveContext = false;
  // When true, render the results list in normal document flow instead of an
  // absolutely-positioned overlay. Set by hosts that mount the search inside a
  // clipped/scrolling overlay (the watchlist popover) where an absolute
  // dropdown would be cut off by the overlay's overflow box on mobile.
  @Input() inlineResults = false;

  query = '';
  results: SearchResult[] = [];
  recentSearches: SearchResult[] = [];
  recentTickers: string[] = [];
  showDropdown = false;
  loading = false;
  cachedIndicator = false;

  private readonly RECENT_TICKERS_KEY = 'recent_tickers';

  private readonly querySubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly HISTORY_KEY = 'hub_search_history';
  // Use relative /api — nginx proxies /api/* → api-gateway:3000
  // Works for ALL access methods: localhost:80, Cloudflare, ngrok, iPad
  private readonly API_BASE = '/api';

  constructor(
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef,
    public readonly tickerCtx: TickerContextService,
  ) {
    this.loadHistory();
    this.loadRecentTickers();

    this.querySubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(q => {
        if (!q || q.length < 2) {
          this.results = [];
          this.loading = false;
          return of(null);
        }
        this.loading = true;
        this.cachedIndicator = false;
        return this.http.get<SearchResult[]>(
          `${this.API_BASE}/search?q=${encodeURIComponent(q)}`,
          { observe: 'response' }
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (resp: any) => {
        if (!resp) return;
        this.loading = false;
        this.results = resp.body || [];
        const cacheHeader: string = resp.headers?.get('x-cache') || '';
        this.cachedIndicator = cacheHeader.startsWith('HIT');
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.results = [];
      }
    });
  }

  onQueryChange(q: string): void {
    this.querySubject.next(q);
    this.showDropdown = true;
  }

  onFocus(): void {
    this.showDropdown = true;
    this.cdr.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Only close if click was truly outside — check both component and any open dropdown
    const insideComponent = target.closest('app-stock-search');
    const insideDropdown = target.closest('.search-dropdown');
    if (!insideComponent && !insideDropdown) {
      this.showDropdown = false;
      this.cdr.detectChanges();
    }
  }

  selectResult(result: SearchResult): void {
    this.saveToHistory(result);
    this.addRecentTicker(result.symbol);
    this.symbolSelected.emit(result);
    this.query = '';
    this.results = [];
    this.showDropdown = false;
  }

  onRecentTickerClick(ticker: string): void {
    this.symbolSelected.emit({ symbol: ticker, name: ticker, type: 'Equity', exchange: '', currency: '' });
  }

  // PIN — active-ticker bar actions. Open re-uses the normal select flow so the
  // parent (top bar) routes to Options Analysis; clear drops the focus context.
  openActiveTicker(): void {
    const t = this.tickerCtx.get();
    if (t) {
      this.symbolSelected.emit({ symbol: t, name: t, type: 'Equity', exchange: '', currency: '' });
    }
  }

  clearActiveTicker(): void {
    this.tickerCtx.clear();
    this.cdr.detectChanges();
  }

  removeRecent(ticker: string): void {
    this.recentTickers = this.recentTickers.filter(t => t !== ticker);
    try {
      localStorage.setItem(this.RECENT_TICKERS_KEY, JSON.stringify(this.recentTickers));
    } catch { /* storage full */ }
    this.cdr.detectChanges();
  }

  clear(): void {
    this.query = '';
    this.results = [];
    this.showDropdown = false;
  }

  clearHistory(): void {
    this.recentSearches = [];
    localStorage.removeItem(this.HISTORY_KEY);
  }

  private loadRecentTickers(): void {
    try {
      const raw = localStorage.getItem(this.RECENT_TICKERS_KEY);
      this.recentTickers = raw ? JSON.parse(raw).slice(0, 5) : [];
    } catch {
      this.recentTickers = [];
    }
  }

  private addRecentTicker(symbol: string): void {
    this.recentTickers = [symbol, ...this.recentTickers.filter(t => t !== symbol)].slice(0, 5);
    try {
      localStorage.setItem(this.RECENT_TICKERS_KEY, JSON.stringify(this.recentTickers));
    } catch { /* storage full */ }
  }

  private loadHistory(): void {
    try {
      const raw = localStorage.getItem(this.HISTORY_KEY);
      this.recentSearches = raw ? JSON.parse(raw).slice(0, 5) : [];
    } catch {
      this.recentSearches = [];
    }
  }

  private saveToHistory(result: SearchResult): void {
    const existing = this.recentSearches.filter(r => r.symbol !== result.symbol);
    this.recentSearches = [result, ...existing].slice(0, 5);
    try {
      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(this.recentSearches));
    } catch { /* storage full — ignore */ }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
